/* eslint-disable no-restricted-imports -- File defines overrides */
import type { FC } from 'react';
import { useEffect, useRef } from 'react';

import type { NavigateFunction } from '@remix-run/react';
import { useNavigate as useRemixNavigate } from '@remix-run/react';
import { LoadingPage } from 'platform/components/LoadingPage';
import { SystemError } from 'platform/Error';
import { useStalePageCheck } from 'platform/hooks/useReloadStalePage';
import { useObservability } from 'platform/observability';
import { useToPath } from 'platform/preserveQueryParams';
import { normalizePathname, usePath } from 'shared/hooks/usePath';
import { trackNavigationEvent } from 'shared/hooks/useTrackPerformance';

import type { NavigateOptions } from './types';

/* eslint-enable no-restricted-imports  -- File defines overrides */

export type { To } from './types';

/**
 * Component that tracks all the redirects and ensures that url params are passed to the new route
 *
 * @override
 */
export const Navigate: FC<
  NavigateOptions & {
    isFallback?: boolean;
    customDisallowedSearchParams?: string[];
  }
> = ({ to, isFallback, customDisallowedSearchParams, ...props }) => {
  const { trackEvent, trackError } = useObservability();
  const from = usePath();
  const checkStalePageAndAuth = useStalePageCheck();
  const navigate = useRemixNavigate();

  if (!to) {
    throw new SystemError('Navigate component must have a properly formatted `to` prop');
  }

  const getToPath = useToPath(customDisallowedSearchParams);

  useEffect(() => {
    let isMounted = true;

    const doNavigate = async () => {
      try {
        const hasNavigated = await checkStalePageAndAuth();
        if (hasNavigated) {
          return;
        }
      } catch (e) {
        trackError('failed to check staleness/auth', e as Error);
      }

      if (!isMounted) {
        return;
      }

      const toPath = getToPath(to);

      trackEvent(
        props.replace ? 'redirect' : 'navigate',
        normalizePathname(toPath.pathname ?? ''),
        {
          from,
          fallback: isFallback,
          ...props?.state,
        },
      );
      trackNavigationEvent();

      navigate(toPath, props);
    };

    void doNavigate();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on load
  }, []);

  return <LoadingPage />;
};

/**
 * Component that tracks all the redirects and ensures that url params are passed to the new route
 * @override
 */
export const useNavigate = (customDisallowedSearchParams?: string[]): NavigateFunction => {
  const navigate = useRemixNavigate();
  const { trackEvent, trackError } = useObservability();
  const from = usePath();
  const getToPath = useToPath(customDisallowedSearchParams);
  const checkStalePageAndAuth = useStalePageCheck();
  const callIdRef = useRef(0);

  return (...props) => {
    const callId = ++callIdRef.current;
    void (async () => {
      try {
        await checkStalePageAndAuth();
      } catch (e) {
        trackError('failed to check staleness/auth', e as Error);
      }

      // A newer navigation call arrived while we were awaiting — abort this one.
      if (callId !== callIdRef.current) {
        return;
      }

      trackNavigationEvent();

      // Numerically navigate the history stack
      if (typeof props[0] === 'number') {
        const delta: number = props[0];
        trackEvent('navigate', `Navigate the history stack by ${delta} `, { from });
        navigate(delta);
        return;
      }

      // Navigate to specific new location while maintaining search params
      const to = props[0];
      const options = props[1] as NavigateOptions | undefined;
      const toPath = getToPath(to);
      trackEvent(
        options?.replace ? 'redirect' : 'navigate',
        normalizePathname(toPath.pathname ?? ''),
        { from, ...options?.state },
      );
      navigate(toPath, options);
    })();
  };
};

/**
 * Fallback component that tracks all the redirects and ensures that url params are passed to the new route. This is a simple extension of the Navigate component defined above
 * @override
 */
export const Fallback: FC<NavigateOptions & { customDisallowedSearchParams?: string[] }> = (
  props,
) => <Navigate {...props} isFallback />;
